import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import {
	type IEnvironment,
	type ReaderSelection,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderArgument,
} from 'relay-runtime';

import { toDocumentNode } from './toDocumentNode';

export type UseWriteFragmentInteropArgs = {
	fragmentSpec: GraphQLTaggedNode;
};

export type UseWriteFragmentInteropResult<TData> = (params: {
	fragmentId: string;
	data: TData;
	variables?: Record<string, any>;
}) => void;

function useWriteFragmentInteropRelay<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec } = args;
	const env = useRelayEnvironment();

	return useCallback(
		({ data, fragmentId, variables }) => {
			writeFragmentShim(env, fragmentSpec, fragmentId, data, variables);
		},
		[env, fragmentSpec],
	);
}

function useWriteFragmentInteropApollo<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec } = args;
	const client = useApolloClient();

	return useCallback(
		({ data, fragmentId, variables }) => {
			client.writeFragment<TData>({
				id: fragmentId,
				fragment: toDocumentNode(fragmentSpec),
				data,
				variables,
			});
		},
		[client, fragmentSpec],
	);
}

export function useWriteFragmentInterop<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	return process.env.BUILD_VARIANT === 'relay'
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropRelay(args)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropApollo(args);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `writeFragment` using Relay
 */
function writeFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	data: TData,
	variables?: Record<string, any>,
): void {
	if (!data) {
		return;
	}

	const relayFragmentId = apolloFragmentIdToRelayFragmentId(fragmentId);

	// Use commitUpdate to write to the store
	environment.commitUpdate((storeProxy) => {
		const record = storeProxy.get(relayFragmentId);
		if (!record) {
			return;
		}

		// Helper to compute storage key for fields with arguments
		const getFieldKey = (
			field: ReaderLinkedField | ReaderScalarField,
			vars: Record<string, any> = {},
		): string => {
			if (!field.args || field.args.length === 0) {
				return field.name;
			}

			// Compute storage key for fields with arguments
			const args = field.args
				.map((arg: ReaderArgument) => {
					let value: string;
					if (arg.kind === 'Literal') {
						value = JSON.stringify(arg.value);
					} else if (arg.kind === 'Variable') {
						value = JSON.stringify(vars[arg.variableName]);
					} else {
						// Fallback for other argument types
						value = JSON.stringify((arg as any).value);
					}
					return `${arg.name}:${value}`;
				})
				.join(',');

			return `${field.name}(${args})`;
		};

		// Define a function to write the fields specified in the fragment
		const writeFields = (
			recordId: string,
			fields: readonly ReaderSelection[],
			writeData: any,
		): void => {
			const targetRecord = storeProxy.get(recordId);
			if (!targetRecord || !writeData) {
				return;
			}

			for (const field of fields) {
				if (field.kind === 'Condition') {
					// Handle @include/@skip directives with variables
					const conditionValue = variables?.[field.condition];
					const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

					if (shouldInclude) {
						// Recursively process the selections within this condition
						writeFields(recordId, field.selections, writeData);
					}
				} else if (field.kind === 'LinkedField') {
					// For linked fields, we need to handle nested data
					const fieldKey = getFieldKey(field, variables ?? {});
					const linkedData = writeData[field.name];
					if (linkedData) {
						const linkedRecord = targetRecord.getLinkedRecord(fieldKey);
						if (linkedRecord) {
							writeFields(linkedRecord.getDataID(), field.selections, linkedData);
						}
					}
				} else if (field.kind === 'ScalarField') {
					const fieldKey = getFieldKey(field, variables ?? {});
					if (writeData[field.name] !== undefined) {
						targetRecord.setValue(writeData[field.name], fieldKey);
					}
				} else if (field.kind === 'InlineFragment') {
					// Handle inline fragments (e.g., ... on SomeType)
					// For write operations, we generally process all inline fragment selections
					writeFields(recordId, field.selections, writeData);
				} else {
					throw new Error(`Unsupported field kind: ${field.kind}`);
				}
			}
		};

		const writerFragment = fragment as any;
		const topLevelFields = writerFragment?.selections;

		if (topLevelFields) {
			writeFields(relayFragmentId, topLevelFields ?? [], data);
		}
	});
}
